import type {
    FeatureCollection,
    Geometry,
    LineString,
    Position
} from 'geojson'
import mapboxgl, { type Point } from 'mapbox-gl'

function getNormal(
    p1: Point,
    p2: Point
) {
    const dx = p2.x - p1.x
    const dy = p2.y - p1.y

    const length = Math.sqrt(dx * dx + dy * dy)

    if (length === 0) {
        return { nx: 0, ny: 0 }
    }

    return {
        nx: dy / length,
        ny: -dx / length
    }
}

function offsetLineScreenSpace(
    map: mapboxgl.Map,
    coordinates: Position[],
    offsetPixels: number
): Position[] {

    if (coordinates.length < 2) {
        return coordinates
    }

    // Projection écran
    const screenPoints = coordinates.map(coord =>
        map.project(coord as [number, number])
    )

    const shifted: mapboxgl.Point[] = []

    for (let i = 0; i < screenPoints.length; i++) {

        let nx = 0
        let ny = 0

        // Premier point
        if (i === 0) {
            const n = getNormal(
                screenPoints[i],
                screenPoints[i + 1]
            )

            nx = n.nx
            ny = n.ny
        }

        // Dernier point
        else if (i === screenPoints.length - 1) {
            const n = getNormal(
                screenPoints[i - 1],
                screenPoints[i]
            )

            nx = n.nx
            ny = n.ny
        }

        // Point intermédiaire
        else {
            const n1 = getNormal(
                screenPoints[i - 1],
                screenPoints[i]
            )

            const n2 = getNormal(
                screenPoints[i],
                screenPoints[i + 1]
            )

            // Moyenne des normales
            nx = n1.nx + n2.nx
            ny = n1.ny + n2.ny

            const len = Math.sqrt(nx * nx + ny * ny)

            if (len !== 0) {
                nx /= len
                ny /= len
            }
        }

        shifted.push(
            new mapboxgl.Point(
                screenPoints[i].x + nx * offsetPixels,
                screenPoints[i].y + ny * offsetPixels
            )
        )
    }

    // Retour géographique
    return shifted.map(p => {
        const lngLat = map.unproject(p)

        return [lngLat.lng, lngLat.lat]
    })
}

export function offsetFeatureCollectionPixelPerfect(
    map: mapboxgl.Map,
    collection: FeatureCollection<Geometry>,
    getOffsetPixels: (
        map: mapboxgl.Map
    ) => number
): FeatureCollection<Geometry> {

    return {
        type: 'FeatureCollection',
        features: collection.features.map(feature => {

            const baseOffset = -getOffsetPixels(map)

            const geometry = feature.geometry as LineString

            const shiftedCoordinates =
                offsetLineScreenSpace(
                    map,
                    geometry.coordinates,
                    baseOffset
                )

            return {
                ...feature,
                geometry: {
                    type: 'LineString',
                    coordinates: shiftedCoordinates
                }
            }
        })
    }
}